<template>
	<ProcessCreatePage>
		<Header :title="title">
			<p v-if="process">{{ $t('process_created_documents') }}</p>
		</Header>

		<div class="main">
			<LoadingSpinner v-if="isLoading" />
			<div v-else class="flex space-x-4">
				<BaseButton v-if="!isUserAllowedInLevel2" @click="printDocument" class="mr-4" :disabled="!pdfURL">
					<font-awesome-icon :icon="['fas', 'redo']" class="mr-2" v-if="pdfURL === undefined" spin />
					{{ $('print_documents_for_customer') }}
				</BaseButton>
				<BaseButton v-if="!isUserAllowedInLevel2" @click="askMailDocument" class="mr-4" :disabled="!pdfURL">
					<font-awesome-icon :icon="['fas', 'redo']" class="mr-2" v-if="pdfURL === undefined" spin />
					{{ $t('send_documents_per_mail_to_customer') }}
				</BaseButton>
				<BaseButton @click="toProcess">{{ $t('to_damage_case') }}</BaseButton>
			</div>
		</div>

		<Footer @backButtonClick="$emit('previous', $event)" :is-skippable="false" :is-back-button="!isSuccess" :is-continue-button="false" />
	</ProcessCreatePage>
</template>

<script>
import Header from '@/components/damagecases/Header';
import Footer from '@/components/damagecases/Footer';
import ProcessRepository from '@/api/repositories/ProcessRepository';
import LoadingSpinner from '@/components/navigation/LoadingSpinner';
import Authentication from '@/mixins/Authentication';
import BaseButton from '@/components/forms/buttons/BaseButton';
import { CustomersApiFactory, DamagecasesApiFactory, PdfApiFactory } from '@/api/generated';
import apiDefaults from '@/api/defaultConfiguration';
import ProcessCreatePage from '@/views/management/Process/ProcessCreatePage';

export default {
	name: 'ProcessCreateComprehensiveFinish',
	components: { BaseButton, ProcessCreatePage, LoadingSpinner, Footer, Header },
	mixins: [Authentication],
	data() {
		return {
			isLoading: true,
			isError: false,
			process: null,
			pdfURL: undefined,
			pdfBlob: null,
			pdfName: '',
		};
	},
	computed: {
		isSuccess() {
			return !this.isError && !this.isLoading;
		},
		title() {
			const prefix = this.$t('new_comprehensive');
			if (this.isLoading) return prefix + this.$t('new_comprehensive_wait');
			else if (this.isError) return prefix + this.$t('new_comprehensive_error');
			else return prefix + this.$t('new_comprehensive_success');
		},
		getComprehensiveType() {
			return this.$store.getters.getProcessCreateComprehensive.insurance === null
				? this.$t('own_damage').toString()
				: this.$t('comprehensive').toString();
		},
	},
	methods: {
		askMailDocument() {
			if (!this.process?.customer?.email) {
				this.$swal({
					title: this.$t('e_mail').toString(),
					text: this.$t('enter_customer_e_mail').toString(),
					icon: 'question',
					cancelButtonText: this.$t('general.cancel'),
					showCancelButton: true,
					confirmButtonText: this.$t('send'),
					input: 'email',
				}).then(result => {
					if (result.value) {
						this.process.customer.email = result.value;
						CustomersApiFactory(apiDefaults.config(), apiDefaults.basePath, apiDefaults.axios)
							.customersIdPut(this.process.customer.id, this.process.customer)
							.then(() => this.mailDocument(this.process.customer.email));
					}
				});
			} else {
				this.mailDocument(this.process?.customer?.email);
			}
		},
		mailDocument(mail) {
			DamagecasesApiFactory(apiDefaults.config(), apiDefaults.basePath, apiDefaults.axios)
				.damagecasesIdEmailEmailGet(this.process.id, mail)
				.then(() => this.$swal(this.$t('general.success').toString(), this.$t('email_sent').toString(), 'success'))
				.catch(error => {
					this.$swal(this.$t('general.error').toString(), this.$t('email_send_error').toString(), 'error');
				});
		},
		printDocument: function() {
			if (this.pdfURL === '') {
				this.$swal(this.$t('general.attention').toString(), this.$t('pdf_not_ready_retry').toString(), 'warning');
			} else {
				if (this.isAppContainer) {
					const reader = new FileReader();
					const name = this.documentName;
					reader.onload = function() {
						const arrayBuffer = this.result;
						// convert the ArrayBuffer to a comma-separated string
						const dataString = new Uint8Array(arrayBuffer).join(',');
						const messageData = {
							fileName: name,
							data: dataString,
						};
						// send the data to Swift
						window.webkit.messageHandlers.pdfHandler.postMessage(JSON.stringify(messageData));
					};
					reader.readAsArrayBuffer(this.pdfBlob);
				} else {
					const viewerUrl = `/blobviewer?blobUrl=${encodeURIComponent(this.pdfURL)}&title=${encodeURIComponent(this.documentName)}`;

					setTimeout(function() {
						window.open(viewerUrl, '_blank');
						window.URL.revokeObjectURL(viewerUrl);
					}, 200);
				}
			}
		},
		toProcess() {
			this.$router.push({
				name: 'management.process.edit',
				params: { processId: this.process.id, customer: this.$route.query.customer_id || this.$route.query.customer },
			});
		},
		loadPrintPdf() {
			return (
				PdfApiFactory(apiDefaults.config(), apiDefaults.basePath, apiDefaults.axios)
					// TODO may define own type for comprehensive if more pdfs are needed.
					.pdfDamagecasesTypeTempSavePost('repair_contract', {
						...this.process,
						// map to variables in pdf
						customerServiceSign: this.process.customerConsultantSign,
						companySign: this.process.customerRepairOrderSign,
					})
					.then(res => {
						if (res.data.uuid) {
							PdfApiFactory(apiDefaults.config(), apiDefaults.basePath, apiDefaults.axios)
								.pdfDamagecasesUuidTempFileGet(res.data.uuid, '_Dokumente.pdf')
								.then(response => {
									const blob = new Blob([response.data], { type: 'application/pdf' });
									if (this.isAppContainer) {
										this.pdfBlob = blob;
									}
									this.pdfName = response.headers['content-disposition'].split('filename=')[1];
									this.pdfURL = URL.createObjectURL(blob);
								});
						} else {
							this.pdfURL = null;
						}
					})
			);
		},
	},
	mounted() {
		/*new ProcessRepository()
			.storeProcessComprehensive(this.$store.getters.getProcessCreateComprehensive)*/
		DamagecasesApiFactory(apiDefaults.config(), apiDefaults.basePath, apiDefaults.axios)
			.damagecasesComprehensivePost(this.$store.getters.getProcessCreateComprehensive)
			.then(response => {
				this.process = { ...this.$store.getters.getProcessCreateComprehensive }; // make a copy, because in finally it will be cleared
				this.process.id = response.data.data.id;
				this.process.imd_net = response.data.data.imdNetId;
				return this.loadPrintPdf();
			})
			.then(() => {
				this.$swal(this.$t('general.success').toString(), this.$t('comprehensive_saved', { type: this.getComprehensiveType }).toString(), 'success');
				this.$store.commit('clearProcessCreateComprehensive');
				this.$store.commit('setConfirmLeaving', false);
			})
			.catch(() => {
				this.isError = true;
				this.$swal(this.$t('general.error').toString(), this.$t('comprehensive_saved_error').toString(), 'error');
			})
			.finally(() => (this.isLoading = false));
	},
};
</script>
